import React, { useState } from 'react';
import { flowRight } from 'lodash/fp';
import { withStyles } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';

import ButtonsForm from 'ui/form/ButtonsForm';

import { withAppContext } from '../../../AppContext';
import { ActionTypes, getLearnMoreUrl } from '../Consts';

export function getActionButtons(type) {
    return ACTIONS[type];
}

const ACTIONS = {
    edit: EditActions,
    view: ViewActions,
    details: DetailsActions,
};

const styles = theme => ({
    learnMore: {
        color: theme.palette.text.blue,
    },
});

const withButtonsProps = flowRight(withStyles(styles), withAppContext);

const Buttons = withButtonsProps(({ identifier, children, app, classes }) => {
    const {
        settings: { brand },
    } = app;
    const learnMoreName = getLearnMoreUrl(identifier);
    const learnMoreUrl = brand[learnMoreName] || '';
    return (
        <ButtonsForm withLeft>
            <Typography>
                <Link
                    className={classes.learnMore}
                    href={learnMoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Learn more
                </Link>
            </Typography>
            <div>{children}</div>
        </ButtonsForm>
    );
});

function EditActions({ identifier, tabs, changes, values, isValid, onAction }) {
    const onReset = () => onAction(ActionTypes.RESET);
    const [loading, setLoading] = useState(false);
    const onSave = async () => {
        const { rules, extra } = values;
        try {
            setLoading(true);
            await onAction(ActionTypes.SAVE, { identifier, rules, extra });
        } finally {
            setLoading(false);
        }
    };
    if (!isValid) {
        return (
            <Buttons identifier={identifier}>
                <ButtonsForm.NextButton
                    disabled={tabs.locked}
                    onClick={tabs.setNext}
                />
            </Buttons>
        );
    }
    if (changes) {
        return (
            <Buttons identifier={identifier}>
                <ButtonsForm.BackButton onClick={onReset} label="Cancel" />
                <ButtonsForm.NextButton
                    label="Save changes"
                    loading={loading}
                    disabled={tabs.locked}
                    onClick={onSave}
                />
            </Buttons>
        );
    }
    return (
        <Buttons identifier={identifier}>
            <ButtonsForm.NextButton label="OK" onClick={onReset} autoFocus />
        </Buttons>
    );
}

function ViewActions({ tabs, identifier, changes, values, onAction }) {
    const onReset = () => onAction(ActionTypes.RESET);
    const [loading, setLoading] = useState(false);
    const onSave = async () => {
        const { rules, extra } = values;
        try {
            setLoading(true);
            await onAction(ActionTypes.SAVE, { identifier, rules, extra });
        } finally {
            setLoading(false);
        }
    };
    const onReload = () => onAction(ActionTypes.RELOAD);
    if (!tabs.isLast) {
        return (
            <Buttons identifier={identifier}>
                {!tabs.isFirst && (
                    <ButtonsForm.BackButton
                        label="Back"
                        onClick={tabs.setPrev}
                    />
                )}
                {tabs.isFirst && (
                    <ButtonsForm.BackButton label="Cancel" onClick={onReset} />
                )}
                <ButtonsForm.NextButton
                    disabled={tabs.locked}
                    onClick={tabs.setNext}
                />
            </Buttons>
        );
    }
    if (changes) {
        return (
            <Buttons identifier={identifier}>
                <ButtonsForm.BackButton onClick={onReset} label="Back" />
                <ButtonsForm.NextButton
                    label="Save changes"
                    disabled={tabs.locked}
                    loading={loading}
                    onClick={onSave}
                />
            </Buttons>
        );
    }
    return (
        <Buttons identifier={identifier}>
            <ButtonsForm.NextButton label="OK" onClick={onReload} />
        </Buttons>
    );
}

function DetailsActions({ identifier, onAction }) {
    const onClose = () => onAction(ActionTypes.RESET);
    return (
        <Buttons identifier={identifier}>
            <ButtonsForm.NextButton label="OK" onClick={onClose} />;
        </Buttons>
    );
}
